import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Sprint } from '../../../models/sprint';
import { UserstoryService } from '../../../services/userstory.service';
import { Issue } from '../../../models/issue';
import { Status } from '../../../models/status';
import { concat } from 'rxjs';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ReleaseplanningSyncService } from '../../../services/synchronization/releaseplanning-sync.service';
import { SprintService } from '../../../services/sprint.service';
import { SettingsService } from '../../../services/settings.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Userstory } from '../../../models/userstory';
import { SprintcardHelper } from '../../sprintcard/sprintcard.helper';
import { StoryPointHelper } from '../../../util/storypoint-helper';
import { SynchronizationService } from '../../../services/synchronization.service';

@Component({
  selector: 'app-releaseplanning-sprint-column',
  templateUrl: './releaseplanning-sprint-column.html',
  styleUrls: ['./releaseplanning-sprint-column.scss']
  })
/** This class represents a team view on the PI board. */
export class ReleaseplanningSprintColumn implements OnInit {

  @ViewChild('issueContainer') issueContainer;
  @Input() sprint: Sprint;
  @Input() projectKey: string;
  @Input() statuses: Status[] = [];
  @Output() sprintRemoved = new EventEmitter<number>();
  issues: Issue[] = [];
  private componentId: string;
  private syncService: ReleaseplanningSyncService;

  constructor(
    private userstoryService: UserstoryService,
    private sprintService: SprintService,
    private baseSyncService: SynchronizationService,
    private settingsService: SettingsService,
    private sprintcardHelper: SprintcardHelper,
    protected translate: TranslateService,
    private snackbar: MatSnackBar
  ) {
    this.issuesArrayInterceptChanges();
    this.syncService = new ReleaseplanningSyncService(baseSyncService);
  }

  ngOnInit() {
    this.syncService.init(this);
    this.setupComponentEventListener();
    this.loadIssues();
  }

  setupComponentEventListener() {
    this.componentId = ReleaseplanningSprintColumn.getReleaseplanningSprintColumnComponentId(this.projectKey, this.sprint.id);
  }

  /**
  * Modifies functions of the 'this.issues'-Array so 'this.updateCalculatedSprintData()'
  * gets executed whenever an issue is added or removed from the array
  */
  private issuesArrayInterceptChanges() {
    const localThis = this;
    ['pop', 'push', 'shift', 'unshift', 'splice'].forEach((operation) => {
      this.issues[operation] = function () {
        const result = Array.prototype[operation].apply(localThis.issues, arguments);
        localThis.updateCalculatedSprintData();
        return result;
      };
    });
  }

  /**
  * Update calculated (not-persisted) data of the sprint, like the sum of storypoints
  * of the userstories of the sprint
  */
  private updateCalculatedSprintData(): void {
    this.setSumOfStorypointsForSprint();
    this.setAllIssuesDoneState();
  }

  public static getReleaseplanningSprintColumnComponentId(projectKey: string, sprintId: number): string {
    return ReleaseplanningSprintColumn.name + ';' + projectKey + ';' + sprintId;
  }

  loadIssues(): void {
    this.userstoryService.getIssuesFromSprint(this.projectKey, this.sprint.id, false, false, 'story, task, bug')
      .subscribe(result => {
        // Clear array and fill it with the new issues without creating a new Array reference in order not to
        // lose our interceptor-functions added by 'this.issuesArrayInterceptChanges()'
        this.issues.length = 0;
        this.issues.push(...result);
      });
  }

  // TODO: drag issue from sprint to backlog needs to be implemented in SP2-backlog
  onIssueDropped(event: CdkDragDrop<Issue[]>): void {
    const sourceContainer = event.previousContainer;
    const sourceSprintId = Number(sourceContainer.element.nativeElement.dataset.sprintid);
    const sourceIsBacklog = (sourceContainer.id === 'product-backlog-issue-container');
    const issue: Issue = event.item.data;

    // If this sprint is closed, dont accept drops
    if (this.sprint.state === 'closed') {
      const messageTranslattionKey = 'actionview.releaseplanning.messageMoveIssueToClosedSprint';
      this.showSnackbarMessage(messageTranslattionKey);
      return;
    }

    // Issue of another sprint or the productbacklog dropped into this sprint. Persist the change.
    if (sourceSprintId || sourceIsBacklog) {
      transferArrayItem(sourceContainer.data, this.issues, event.previousIndex, event.currentIndex);
      this.moveParentissueToThisSprint(issue);
      return;
    }

    // Issue of this sprint dropped into this sprint. Just change the issue order inside this sprint
    if (sourceSprintId === this.sprint.id) {
      moveItemInArray(this.issues, event.previousIndex, event.currentIndex);
      this.userstoryService.changeRank(this.projectKey, <Userstory>issue, <Userstory[]>this.issues).subscribe();
      return;
    }
  }

  onCardMoved(event) {
    // TODO reimplement with new drag-preview
  }

  protected onIssueModified(): void {
    this.updateCalculatedSprintData();
  }

  onIssueRemoved(id: number): void {
    this.issues.forEach((issue, index) => {
      if (Number(issue.id) === Number(id)) {
        this.issues.splice(index, 1);
      }
    });
  }

  /**
   * Moves a parentissue from the backlog or another sprint to this sprint (the sprint of this component)
   **/
  private moveParentissueToThisSprint(issue: Issue): void {
    const projectKey = this.settingsService.getProjectName();
    concat(
      this.userstoryService.assignIssuesToSprint(projectKey, this.sprint.id.toString(), [<Userstory>issue]),
      this.userstoryService.changeRank(projectKey, <Userstory>issue, <Userstory[]>this.issues)
    ).subscribe();
  }

  public startSprint(sprint: Sprint): void {
    sprint.state = 'active';
    this.sprintService.updateSprint(this.projectKey, sprint);
  }

  public finishSprint(sprint: Sprint): void {
    if (sprint['allIssuesDone']) {
      sprint.state = 'closed';
      this.sprintService.updateSprint(this.projectKey, sprint);
    } else {
      const messageTranslattionKey = 'actionview.releaseplanning.messageCantFinishSprint';
      this.showSnackbarMessage(messageTranslattionKey);
    }
  }

  /**
   * Calculate the sum of story points for a sprint and add the the data to the sprint-object.
   */
  private setSumOfStorypointsForSprint(): void {
    this.sprint['sumOfStorypoints'] = StoryPointHelper.getSumOfStorypoints(this.issues);
    this.sprint['sumOfStorypointsDone'] = StoryPointHelper.getSumOfStorypoints(this.issues, true);
  }

  /**
   * Check with the backend if all issues (incl. subissues) of this sprint have state = done
   * and set this flag on sprint (used to determine if sprint can be finished or not).
   */
  private setAllIssuesDoneState(): void {
    this.sprintService.checkAllIssuesDone(this.getProjectName(), this.sprint.id).subscribe(result => {
      this.sprint['allIssuesDone'] = <boolean>result;
    });
  }

  public openSprintcard(mouseEvent: MouseEvent): void {
    this.sprintcardHelper.createCard(this.sprint, this.projectKey, this, null, mouseEvent, null);
  }


  private showSnackbarMessage(messageTranslationKey: string): void {
    const message = this.translate.instant(messageTranslationKey);
    this.snackbar.open(message, 'Ok', { duration: 4000, verticalPosition: 'bottom' });
  }



  public getProjectName(): string {
    return this.settingsService.getProjectName();
  }
}

